/* import __COLOCATED_TEMPLATE__ from './site-footer.hbs'; */
import { action, computed } from '@ember/object';
import { bool, match, notEmpty, or, reads } from '@ember/object/computed';
import { classNameBindings, classNames, tagName } from '@ember-decorators/component';
import { isPresent } from '@ember/utils';
import { service } from '@ember/service';
import Component from '@ember/component';
import classic from 'ember-classic-decorator';
import styles from './site-footer.module.scss';

@classic
@tagName('footer')
@classNames('site-footer', styles.component)
@classNameBindings('isEditMode:edit-mode')
export default class SiteFooter extends Component {
  @service analytics;
  @service currentPractice;
  @service intl;
  @service router;
  @service session;
  @service websiteWidget;

  @reads('currentPractice.defaultMarketingOffice') defaultMarketingOffice;
  @reads('currentPractice.defaultOffice') defaultOffice;
  @reads('currentPractice.defaultPhoneNumber') defaultPhoneNumber;
  @reads('currentPractice.hasCustomDomain') hasCustomDomain;
  @bool('office') hasOffice;
  @bool('practiceWebsite') hasPracticeWebsite;
  @or('practiceWebsite.practiceName', 'currentPractice.fullName') practiceName;
  @reads('practiceWebsite.showSectionLocations') showSectionLocations;
  @notEmpty('socialMediaLinks') showSocialMedia;
  @reads('router.currentRouteName') currentRouteName;
  @match('currentRouteName', /home/) isHomeRoute;

  @computed('practiceWebsite.practiceContactForm', 'currentPractice.practiceContactForm')
  get showContactCta() {
    return (
      this.practiceWebsite?.practiceContactForm ?? this.currentPractice.get('practiceContactForm')
    );
  }

  @computed('currentPractice.practiceWebsite', 'edit', 'model')
  get practiceWebsite() {
    return this.edit
      ? this.model // NOTE: use draft practiceWebsite model when in website editor
      : this.currentPractice.get('practiceWebsite');
  }

  @computed('practiceWebsite', 'isHomeRoute')
  get hideHelpCenterLink() {
    return this.isHomeRoute && isPresent(this.practiceWebsite);
  }

  @computed('defaultOffice.city', 'hasPracticeWebsite', 'practiceWebsite.practiceCity')
  get city() {
    return this.hasPracticeWebsite ? this.practiceWebsite.practiceCity : this.defaultOffice?.city;
  }

  @computed('hasCustomDomain')
  get linkRel() {
    return this.hasCustomDomain ? 'sponsored' : 'noopener noreferrer nofollow';
  }

  @computed(
    'defaultMarketingOffice',
    'defaultOffice',
    'edit',
    'marketingOffice',
    'hasPracticeWebsite'
  )
  get office() {
    if (this.edit) {
      return this.marketingOffice; // NOTE: use draft marketingOffice when in website editor
    }
    return this.hasPracticeWebsite ? this.defaultMarketingOffice : this.defaultOffice;
  }

  @computed('defaultPhoneNumber', 'hasPracticeWebsite', 'practiceWebsite.practicePhone')
  get phone() {
    if (this.hasPracticeWebsite) {
      return this.practiceWebsite.practicePhone;
    }
    return this.defaultPhoneNumber;
  }

  @computed('hasOffice', 'hasPracticeWebsite', 'showSectionLocations')
  get showLocation() {
    if (this.hasPracticeWebsite) {
      return this.showSectionLocations && this.hasOffice;
    }
    return this.hasOffice;
  }

  @computed
  get showCookiePreferencesLink() {
    return window.DG_BANNER_API && window.DG_BANNER_API.showConsentBanner;
  }

  @computed(
    'hasPracticeWebsite',
    'practiceWebsite.{facebookUrl,twitterUrl,instagramUrl,yelpUrl,youtubeUrl,linkedinUrl}'
  )
  get socialMediaLinks() {
    if (!this.hasPracticeWebsite) {
      return undefined;
    }

    let { practiceWebsite } = this;

    return [
      {
        title: this.intl.t('practiceWebsite.socialMedia.facebook'),
        url: practiceWebsite.facebookUrl,
      },
      {
        title: this.intl.t('practiceWebsite.socialMedia.x'),
        url: practiceWebsite.twitterUrl,
      },
      {
        title: this.intl.t('practiceWebsite.socialMedia.instagram'),
        url: practiceWebsite.instagramUrl,
      },
      {
        title: this.intl.t('practiceWebsite.socialMedia.yelp'),
        url: practiceWebsite.yelpUrl,
      },
      {
        title: this.intl.t('practiceWebsite.socialMedia.youtube'),
        url: practiceWebsite.youtubeUrl,
      },
      {
        title: this.intl.t('practiceWebsite.socialMedia.linkedin'),
        url: practiceWebsite.linkedinUrl,
      },
    ].filterBy('url');
  }

  @action
  trackAccessOn(linkName) {
    let linkDescription;

    switch (linkName) {
      case 'Privacy':
        linkDescription = 'privacy policy';
        break;
      case 'Terms':
        linkDescription = 'terms';
        break;
      case 'License':
        linkDescription = 'license agreement';
        break;
      case 'Help Center':
        linkDescription = 'help center';
        break;
      default:
        linkDescription = 'unknown';
    }

    let eventName = 'Footer Link Accessed';
    let eventProperties = {
      object: 'Footer Link',
      action: 'Accessed',
      'link_type': linkDescription,
    };

    this.session.currentClient
      ? this.analytics.track(eventName, eventProperties)
      : this.analytics.trackAnonymously(eventName, eventProperties);
  }

  @action
  openCookiePreferences() {
    window.DG_BANNER_API.showConsentBanner();
  }

  @action
  showContactForm() {
    this.websiteWidget.openContactWidgetTask.perform();
  }
}
